import { useEffect, useRef, useState } from "react";

import BottomSubmit from "../../components/BottomSubmit";
import ConfigLoader from "../../components/ConfigLoader";

import './styles.css'

import api from '@helpers/api'

import CommunityAssetImageField from "@features/uploadAsset/components/CommunityAssetImageField";
import { AssetCategory } from "@features/uploadAsset/constants/assetCategories";

import { Button, Input, Popper, Text } from "@fragments";

import { IconBrandInstagram, IconBrandTiktok, IconBrandWhatsapp, IconChevronDown, IconGeometry, IconBrandYoutube, IconCheck, IconDots, IconHelpCircle, IconLink, IconMail, IconPencil, IconPhoto, IconPlus, IconTrash, IconWallpaper, IconWorld, IconLock } from "@tabler/icons-react";


import { useCommunity } from "contexts/community";

import { useParams } from "react-router-dom";

const LINK_TYPES = [
    { value: 'website', label: 'Site', icon: <IconWorld size={18} /> },
    { value: 'support', label: 'Suporte', icon: <IconHelpCircle size={18} /> },
    { value: 'whatsapp', label: 'WhatsApp', icon: <IconBrandWhatsapp size={18} /> },
    { value: 'instagram', label: 'Instagram', icon: <IconBrandInstagram size={18} /> },
    { value: 'tiktok', label: 'TikTok', icon: <IconBrandTiktok size={18} /> },
    { value: 'youtube', label: 'YouTube', icon: <IconBrandYoutube size={18} /> },
    { value: 'email', label: 'E-mail', icon: <IconMail size={18} /> },
    { value: 'default', label: 'Outro', icon: <IconLink size={18} /> },
];

const LINK_TYPE_LABEL = LINK_TYPES.reduce((acc, t) => {
    acc[t.value] = t.label;
    return acc;
}, {});

const LINK_TYPE_ICON = LINK_TYPES.reduce((acc, t) => {
    acc[t.value] = t.icon;
    return acc;
}, {});

const FREE_TITLE_TYPES = new Set(['website', 'default']);

const VALID_LINK_TYPES = new Set(LINK_TYPES.map((t) => t.value));

function defaultTitleForType(type) {
    return FREE_TITLE_TYPES.has(type) ? '' : (LINK_TYPE_LABEL[type] || '');
}

/**
 * Older backend responses returned community links with `title` and `type`
 * swapped (legacy bug in CommunityResource). Detect that shape and put them
 * back in the right slots so the next save persists the correct payload.
 */
function healLink(link) {
    if (!link || typeof link !== 'object') return link;
    const typeIsValid = VALID_LINK_TYPES.has(link.type);
    const titleLooksLikeType = VALID_LINK_TYPES.has(link.title);
    if (!typeIsValid && titleLooksLikeType) {
        return { ...link, title: link.type, type: link.title };
    }
    return link;
}

/**
 * Best-effort detection of the link type from a URL.
 * Returns one of the LINK_TYPES values, or null if we can't tell.
 */
function detectLinkType(rawUrl) {
    if (!rawUrl || typeof rawUrl !== 'string') return null;
    const value = rawUrl.trim().toLowerCase();
    if (!value) return null;

    if (value.startsWith('mailto:') || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        return 'email';
    }

    // Normalize to a hostname when possible
    let host = '';
    try {
        const candidate = value.includes('://') ? value : 'https://' + value;
        host = new URL(candidate).hostname.replace(/^www\./, '');
    } catch (_) {
        host = value;
    }

    if (/(^|\.)wa\.me$|whatsapp\.com$|whatsapp\.net$/.test(host)) return 'whatsapp';
    if (/(^|\.)instagram\.com$|(^|\.)instagr\.am$/.test(host)) return 'instagram';
    if (/(^|\.)tiktok\.com$/.test(host)) return 'tiktok';
    if (/(^|\.)youtube\.com$|(^|\.)youtu\.be$/.test(host)) return 'youtube';

    if (/(^|\.)(help|support|atendimento|suporte)\./.test(host) || /\/(help|support|suporte|atendimento)(\/|$)/.test(value)) {
        return 'support';
    }

    if (value.startsWith('http://') || value.startsWith('https://')) return 'website';

    return null;
}



export default function General({ info, handleRefresh }) {
    const [hasUpdates, setHasUpdates] = useState(false)
    const [loading, setLoading] = useState(true)

    let { slug } = useParams();

    const { loading: communityLoading, community, loadCommunities } = useCommunity();

    const REF_config = useRef()
    const REF_popper = useRef()
    const REF_statusPopper = useRef()

    const communityId = community?.getBySlug?.(slug)?.id;
    const initialStatus = info?.status ?? community?.getBySlug?.(slug)?.status ?? 'draft';
    const [status, setStatus] = useState(initialStatus);


    useEffect(() => {
        if (!communityLoading) handleLoad(true)
    }, [communityLoading])

    async function handleLoad() {
        if (info) {
            REF_config.current = {
                ...info,
                links: Array.isArray(info.links) ? info.links.map(healLink) : [],
            }
            setLoading(false)
            setStatus(info.status ?? initialStatus);
        }
    }

    async function handleSubmit() {
        try {
            const payload = getPayload();
            if (!payload) return;

            const { data } = await api.patch(`/community/${communityId}`, payload)

            if (data?.id) {
                window.setSnackbar('Alterações salvas')
                setHasUpdates(false)
                loadCommunities?.()
                handleRefresh?.()
            } else {
                window.setSnackbar('Houve um erro ao salvar as alterações, tente novamente mais tarde')
            }
        } catch (e) {
            console.log(e)
            window.setSnackbar('Houve um erro ao salvar as alterações, tente novamente mais tarde')
        }

        function getPayload() {
            const config = REF_config.current;
            const payload = {};

            // Name (required, 3-25 chars)
            if (!config.name || config.name.trim().length === 0) {
                window.setSnackbar('O nome da comunidade é obrigatório');
                return null;
            }

            const nameLength = config.name.trim().length;
            if (nameLength < 3 || nameLength > 25) {
                window.setSnackbar('O nome da comunidade deve ter entre 3 e 25 caracteres');
                return null;
            }

            payload.name = config.name.trim();

            // Description (optional, nullable)
            const description = (config.description ?? '').trim();
            payload.description = description.length > 0 ? description : null;

            // Links (optional, max 10)
            if (config.links && config.links.length > 0) {
                if (config.links.length > 10) {
                    window.setSnackbar('Você pode adicionar no máximo 10 links');
                    return null;
                }
                payload.links = config.links.map((l) => ({
                    title: l.title,
                    url: l.url,
                    type: l.type ?? 'website',
                }));
            }

            // Banner & hero: URLs already uploaded via the presign flow.
            if (config.banner !== undefined) payload.banner = config.banner || null;
            if (config.hero !== undefined) payload.hero = config.hero || null;
            if (config.hero_condensed !== undefined) payload.hero_condensed = config.hero_condensed || null;

            return payload;
        }
    }

    function handleChange(key, value) {
        REF_config.current[key] = value
        setHasUpdates(true)
    }

    async function handleChangeStatus(next) {
        if (next === status) return;
        try {
            const { data } = await api.patch(`/community/${communityId}/status`, { status: next });
            if (data?.id) {
                setStatus(next);
                window.setSnackbar(next === 'published' ? 'Comunidade publicada' : 'Comunidade despublicada');
                loadCommunities?.();
                handleRefresh?.();
            } else {
                window.setSnackbar('Não foi possível alterar o status, tente novamente');
            }
        } catch (e) {
            console.log(e);
            window.setSnackbar('Não foi possível alterar o status, tente novamente');
        }
    }

    function statusOptionLabel(title, description) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 14, fontWeight: 500 }}>{title}</span>
                <span style={{ fontSize: 12, opacity: 0.6, lineHeight: 1.3 }}>{description}</span>
            </div>
        );
    }

    function handleOpenStatus(event) {
        const anchor = event.currentTarget;
        REF_statusPopper.current?.open(
            [
                {
                    id: 'published',
                    label: statusOptionLabel(
                        'Publicado',
                        'Sua comunidade fica visível a todos na internet',
                    ),
                    variant: status === 'published' ? 'info' : undefined,
                    icon: <IconWorld size={20} />,
                    onClick: async () => {
                        await handleChangeStatus('published');
                    },
                },
                {
                    id: 'draft',
                    label: statusOptionLabel(
                        'Rascunho',
                        'Visível apenas para você e criadores convidados',
                    ),
                    variant: status === 'draft' ? 'info' : undefined,
                    icon: <IconGeometry size={20} />,
                    onClick: async () => {
                        await handleChangeStatus('draft');
                    },
                },
            ],
            'bottom-end',
            anchor,
        );
    }

    function handleDelete() {
        window.openConfirm?.({
            title: 'Excluir comunidade',
            headline: 'Excluir comunidade?',
            subtitle: 'A exclusão é definitiva e cancela as assinaturas dos membros. Esta ação não pode ser desfeita.',
            buttons: [
                {
                    text: 'Excluir',
                    variant: 'contained',
                    color: 'error',
                    onClick: async () => {
                        try {
                            await api.delete(`/community/${communityId}`);
                            window.setSnackbar('Comunidade excluída');
                            loadCommunities?.(() => window.navigate?.('/'));
                        } catch (e) {
                            console.log(e);
                            window.setSnackbar('Não foi possível excluir a comunidade');
                        }
                    },
                },
                {
                    text: 'Cancelar',
                    variant: 'text',
                    color: 'overlay',
                },
            ],
        });
    }

    function handleOpenActions(event) {
        const anchor = event.currentTarget;
        REF_popper.current?.open(
            [
                {
                    id: 'delete',
                    label: 'Excluir comunidade',
                    icon: <IconTrash size={18} />,
                    variant: 'danger',
                    onClick: async () => {
                        handleDelete();
                    },
                },
            ],
            'bottom',
            anchor,
        );
    }

    function Hero() {
        return (
            <>
                <Text.h3
                    value="Banner"
                    left={<IconWallpaper />}
                />
                <Text.h4
                    contrast={5}
                    value="Adicione um banner no tamanho 1600px por 300px (16:3)"
                />
                <CommunityAssetImageField
                    className="mt-2"
                    variant="dark"
                    aspectRatio={16 / 3}
                    fullWidth
                    imageMaxWidth={1700}
                    communityId={communityId}
                    category={AssetCategory.COMMUNITY_HERO}
                    value={REF_config.current.hero}
                    onChange={(url) => handleChange('hero', url)}
                    title="Banner da comunidade"
                />

                <Text.h4
                    contrast={5}
                    className="mt-3"
                    value="Banner condensado para mobile e tablet (6:4) — opcional"
                />
                <CommunityAssetImageField
                    className="mt-2 upload-banner"
                    variant="dark"
                    aspectRatio={6 / 4}
                    imageMaxWidth={1200}
                    communityId={communityId}
                    category={AssetCategory.COMMUNITY_HERO_CONDENSED}
                    value={REF_config.current.hero_condensed}
                    onChange={(url) => handleChange('hero_condensed', url)}
                    title="Banner condensado"
                />
            </>
        )
    }

    function Profile() {
        return (
            <>
                <Text.h3
                    value="Foto de perfil"
                    left={<IconPhoto />}
                    className="mt-4 mb-2"
                />
                <Text.h5
                    contrast={5}
                    value="Adicione uma foto de perfil no tamanho 720px por 720px (1:1)"
                />
                <CommunityAssetImageField
                    className="mt-2 upload-banner"
                    variant="dark"
                    size={135}
                    communityId={communityId}
                    category={AssetCategory.COMMUNITY_BANNER}
                    value={REF_config.current.banner}
                    onChange={(url) => handleChange('banner', url)}
                    title="Foto de perfil"
                />
            </>
        )
    }

    function Links() {
        const [links, setLinks] = useState(REF_config.current.links)

        const LinkInfo = ({ data, index }) => {
            const initialType = data.type || 'website';
            const initialTitle = FREE_TITLE_TYPES.has(initialType)
                ? (data.title || '')
                : (data.title || defaultTitleForType(initialType));

            const [open, setOpen] = useState(!!data.open);
            const [title, setTitle] = useState(initialTitle);
            const [link, setLink] = useState(data.url);
            const [type, setType] = useState(initialType);
            // Track whether the user manually picked a type; if so, stop
            // auto-detecting from the URL on subsequent changes.
            const [typeAutoFilled, setTypeAutoFilled] = useState(!data.type || data.type === 'website');

            // Keep REF_config in sync with the (possibly auto-filled) initial title.
            useEffect(() => {
                if (REF_config.current.links?.[index]) {
                    REF_config.current.links[index].title = initialTitle;
                    REF_config.current.links[index].type = initialType;
                }
                // eslint-disable-next-line react-hooks/exhaustive-deps
            }, []);

            useEffect(() => {
                REF_config.current.links[index].open = open;
            }, [open]);

            // Switch the effective title whenever the type changes between
            // free-title and forced-default-title categories.
            function applyTypeChange(nextType, linkCopy) {
                linkCopy[index].type = nextType;
                if (!FREE_TITLE_TYPES.has(nextType)) {
                    const forced = defaultTitleForType(nextType);
                    linkCopy[index].title = forced;
                    setTitle(forced);
                }
                setType(nextType);
            }

            if (!open)
                return (
                    <div className="description-link-row">
                        <div className="description-link bsf-9">
                            <span className="url-icon" style={{ display: 'inline-flex' }}>
                                {LINK_TYPE_ICON[type] || <IconLink size={18} />}
                            </span>
                            <span className="title">{title || <i>(Não configurado)</i>}</span>
                            <span className="url">{link}</span>
                            <span className="type" style={{ opacity: 0.6, marginLeft: 8 }}>
                                {LINK_TYPE_LABEL[type] || type}
                            </span>
                            <Button
                                color="overlay"
                                onClick={() => setOpen(true)}
                                size="small"
                                icon={<IconPencil />}
                            />
                        </div>
                    </div>
                );

            return (
                <div className="link-info-block mt-2 mb-3 px-2 py-2">
                    <Input.Select
                        label="Tipo do link"
                        value={type}
                        options={LINK_TYPES}
                        onInput={(value) => {
                            const linkCopy = [...links];
                            applyTypeChange(value, linkCopy);
                            REF_config.current.links = [...linkCopy];
                            // Manual pick — disable further auto-detection
                            // on this link from URL edits.
                            setTypeAutoFilled(false);
                        }}
                    />

                    {FREE_TITLE_TYPES.has(type) && (
                        <Input.Text
                            className="mt-2"
                            emoji
                            variant="dark"
                            size={25}
                            value={title}
                            onInput={(value) => {
                                const linkCopy = [...links];
                                linkCopy[index].title = value;
                                REF_config.current.links = [...linkCopy];
                                setTitle(value);
                            }}
                            label="Título do link"
                            placeholder="O título do link será o texto do botão no app"
                        />
                    )}

                    <Input.Text
                        className="mt-2"
                        start="https://"
                        variant="dark"
                        size={180}
                        value={link}
                        onInput={(value) => {
                            const linkCopy = [...links];
                            linkCopy[index].url = value;
                            setLink(value);

                            // Auto-fill type from URL only if the user hasn't
                            // explicitly chosen a type already.
                            if (typeAutoFilled) {
                                const detected = detectLinkType(value);
                                if (detected && detected !== type) {
                                    applyTypeChange(detected, linkCopy);
                                }
                            }

                            REF_config.current.links = [...linkCopy];
                        }}
                        label="Link"
                        placeholder="https://seusite.com"
                    />
                    <div className="mt-1 link-buttons-footer">
                        <Button
                            color="error"
                            onClick={() => {
                                const linkCopy = [...links];
                                linkCopy.splice(index, 1);
                                REF_config.current.links = [...linkCopy];
                                setLinks([...linkCopy]);
                            }}
                            endIcon={<IconTrash />}
                            text={"Excluir"}
                        />
                        <Button
                            color="overlay"
                            onClick={() => {
                                setOpen(false);
                            }}
                            endIcon={<IconCheck />}
                            text={"Concluir edição"}
                        />
                    </div>
                </div>
            );
        };

        const linksElm =
            links?.length > 0 ? (
                <>
                    {links?.map((linkInfo, i) => (
                        <LinkInfo data={linkInfo} key={i} index={i} />
                    ))}
                </>
            ) : null;

        return (
            <div>
                <Text.h3
                    value="Links na descrição"
                    className="mt-4 mb-2"
                    left={<IconLink />}
                    grow
                    right={<Button
                        color="white"
                        variant="rounded"
                        onClick={() => {
                            const linksCopy = [...(links || [])];
                            linksCopy.push({ title: "", url: "", type: 'website', open: true });
                            handleChange('links', linksCopy)
                            setLinks([...linksCopy]);
                        }}
                        small
                        disabled={links?.length >= 10}
                        endIcon={<IconPlus />}
                        text="Adicionar link"
                    />}
                />
                {linksElm}
            </div >
        );
    }

    return (
        <div className="config-page config-general-community mt-5" key={loading}>
            <Text.h2
                value="Configurações gerais da comunidade"
                grow
                right={
                    <div style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                        <Button
                            small
                            color={status === 'published' ? 'success' : 'overlay'}
                            onClick={handleOpenStatus}
                            startIcon={status === 'published' ? <IconWorld /> : <IconGeometry />}
                            endIcon={<IconChevronDown />}
                            text={status === 'published' ? 'Publicado' : 'Rascunho'}
                            size="small"
                        />
                        <Button
                            color="overlay"
                            size="small"
                            onClick={handleOpenActions}
                            icon={<IconDots />}
                        />
                    </div>
                }
            />
            <Popper controlRef={REF_popper} />
            <Popper controlRef={REF_statusPopper} />

            {loading ? <ConfigLoader /> : (
                <div className="mt-4">
                    <Input.Text
                        variant="dark"
                        className="mt-2"
                        label="Nome da comunidade"
                        size={25}
                        value={REF_config.current.name}
                        onInput={(v) => {
                            handleChange('name', v)
                        }}
                    />
                    <Input.Text
                        variant="dark"
                        className="mt-2"
                        label="Descrição (opcional)"
                        size={250}
                        multiline
                        value={REF_config.current.description ?? ''}
                        onInput={(v) => {
                            handleChange('description', v)
                        }}
                        emoji
                    />

                    <Hero />

                    <Profile />

                    <Links />
                </div>
            )
            }

            {hasUpdates && <BottomSubmit onPress={handleSubmit} />}
        </div >
    )
}
